import { ClinicEditDrawer } from '@app/icare/routes/_app/kliniken/-components/ClinicEditDrawer'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Q, QueryBaseSchema } from '@pkg/api/query'
import { defineEntityConfig } from '@pkg/db/schema/EntityConfig'
import { ButtonLink } from '@pkg/ui/button'
import { LinkIndicatorIcon } from '@pkg/ui/common'
import { FormattedNameAndAddress } from '@pkg/ui/formattedContent/NameAndAddress'
import { FaHospital } from 'react-icons/fa6'
import { z } from 'zod'
import { ClinicSchema, MedicalSpecialityEnum } from './schema/Clinic'

const label = t`Klinik`

export const ClinicConfig = defineEntityConfig(() => ({
  schema: ClinicSchema,

  label,
  labelPlural: t`Kliniken`,

  icon: FaHospital,

  route: {
    list: '/kliniken',
    edit: '/kliniken/$id',
    new: '/kliniken/new',
  },

  drawer: { modal: ClinicEditDrawer },

  relationSelectionColumns: [
    {
      key: 'clinic.name',
      title: label,
      sorter: true,
      render: (_, row) => <FormattedNameAndAddress name={row.name} address={row.address ?? undefined} />,
    },
    {
      key: 'clinic.speciality',
      sorter: true,
      title: <Trans>Art der Klinik</Trans>,
      render: (_, row) => row.speciality && MedicalSpecialityEnum.$.getLabel(row.speciality),
    },
  ],

  indexColumns: [
    {
      key: 'clinic.name',
      title: label,
      sorter: true,
      fixed: 'start',
      render: (_, row) => (
        <FormattedNameAndAddress
          name={row.name}
          address={row.address ?? undefined}
          link={{ to: '/kliniken/$id', params: { id: row.id } }}
        />
      ),
    },
    {
      key: 'clinic.speciality',
      sorter: true,
      title: <Trans>Art der Klinik</Trans>,
      render: (_, row) => row.speciality && MedicalSpecialityEnum.$.getLabel(row.speciality),
    },
    {
      key: 'actions',
      title: <Trans>Aktionen</Trans>,
      sorter: false,
      fixed: 'end',
      render: (_, row) => (
        <ButtonLink to="/kliniken/$id" params={{ id: row.id }} size="small" shape="round">
          <Trans>Anzeigen</Trans>
          <LinkIndicatorIcon />
        </ButtonLink>
      ),
    },
  ],

  search: {
    filterSchema: QueryBaseSchema.extend({
      sortBy: z
        .enum([
          'clinic.name',
          'clinic.speciality',
          'clinic.address.streetAddress',
          'clinic.address.zipCode',
          'clinic.address.city',
        ])
        .nullish(),
      filters: z
        .object({
          speciality: Q.in(z.enum(MedicalSpecialityEnum.$.zodEnum())).nullish(),
        })
        .optional(),
    }),
    sortableFields: ['name', 'speciality', 'createdAt'] as const,
    defaultSortBy: 'name',
    defaultSortOrder: 'asc',
  },

  format: {
    primary: val => val?.name,
    secondary: val => MedicalSpecialityEnum.$.getLabel(val.speciality),
  },
}))
